import * as React from 'react';
import { useTranslate, useLocale, useSetLocale, Title } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import 'moment-duration-format';
import { DateRangePicker } from 'react-date-range';
import { addDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { format } from 'd3-format';
import _ from 'underscore';
// @ts-ignore
import { TimeSeries, TimeRange, avg, percentile, median } from 'pondjs';

import {
  ChartContainer,
  ChartRow,
  Charts,
  YAxis,
  LineChart,
  Baseline,
  Resizable,
  AreaChart,
  styler,
  Brush,
  LabelAxis,
  ValueAxis,
  ScatterChart,
  Legend,
} from 'react-timeseries-charts';
import { getDataProvider } from '../dataProvider/combined';
import { querySensorData } from '../dataProvider/customRest';
import Select from 'react-select';

// React Timeseries Charts
// http://software.es.net/react-timeseries-charts/#/guide/intro

const baselineStyles = {
  base: {
    stroke: 'steelblue',
    opacity: 0.5,
    width: 0.2,
  },
};

// d3 formatter to display the base with one decimal place
const baseFormat = format('.2f');

class SensorData extends React.Component<any, any> {
  constructor(props) {
    super(props);

    const pathExists = this.props.match.params.datasetId !== undefined;
    console.info(`pathExists: ${pathExists}`);
    const datasetId = pathExists ? this.props.match.params.datasetId : null;
    const selectedDataset = pathExists
      ? {
          label: datasetId,
          value: datasetId,
        }
      : {};
    const now = new Date();
    const selectionRange = {
      startDate: addDays(now, -1),
      endDate: now,
      key: 'selectionRange',
    };
    const datasets = [];

    // @ts-ignore
    const initialRange = new TimeRange([75 * 60 * 1000, 125 * 60 * 1000]);

    // Storage for all the data dataSetting
    const maxColumnNum = 10;
    const dataSetting = Object.fromEntries(
      _.range(maxColumnNum).map((i) => {
        return [`v${i}`, {}];
      })
    );

    this.state = {
      selectedDataset,
      datasets,
      selectionRange,
      ready: false,
      loading: false,
      mode: 'dataSetting',
      dataSetting,
      series: null,
      columnNames: [],
      tracker: null,
      timerange: initialRange,
      brushrange: initialRange,
      style: null,
    };
    this.handleSelectDataset = this.handleSelectDataset.bind(this);
    this.handleSelectDateRange = this.handleSelectDateRange.bind(this);
  }

  async getDatasets(): Promise<Array<object>> {
    const dp = getDataProvider('datasets');
    const res = await dp.getList('datasets', {
      filter: null,
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'id', order: 'asc' },
    });
    return res.data;
  }

  async updateSensorData(datasetId: string, start: number, end: number) {
    if (datasetId === null) return;
    querySensorData(datasetId, start, end)
      .then((sensorData) => {
        // @ts-ignore
        const { dataSetting, datasets } = this.state;
        const colors = ['#003f5c', '#58508d', '#bc5090', '#ff6361', '#ffa600'];
        const dataset = datasets.find((ds) => ds.id === datasetId);
        const columnNames = sensorData.columns.filter((c) => c !== 'time');
        const columnMapV = Object.fromEntries(columnNames.map((c) => [c, c]));
        const columnMap =
          dataset === undefined || dataset.column_map === undefined
            ? columnMapV
            : {
                ...columnMapV,
                ...Object.fromEntries(
                  dataset.column_map.map((m) => [m.key, m.name])
                ),
              };
        console.info(`columnMap: ${JSON.stringify(columnMap)}`);
        const style = styler(
          columnNames.map((c, i) => {
            return { key: c, color: colors[i % colors.length] };
          })
        );
        const series = new TimeSeries(sensorData);

        // Make the TimeSeries here from the points collected above
        for (let columnName of columnNames) {
          // Some simple statistics for each channel
          dataSetting[columnName] = {
            // @ts-ignore
            avg: series.avg(columnName),
            // @ts-ignore
            max: series.max(columnName),
            // @ts-ignore
            min: series.min(columnName),
            show: true,
            units: '',
            label: columnMap[columnName],
          };
        }

        // Min and max time constraints for pan/zoom, along with the smallest timerange
        // the user can zoom into. These are passed into the ChartContainers when we come to
        // rendering.
        // @ts-ignore
        const timerange = series.range();
        if (timerange) {
          const minTime = timerange.begin();
          const maxTime = timerange.end();
          const minDuration = 10 * 60 * 1000;
          this.setState({
            ready: true,
            dataSetting,
            minTime,
            maxTime,
            minDuration,
            style,
            series,
            timerange,
            brushrange: timerange,
            columnNames,
          });
        } else {
          alert('No data found');
          this.setState({ ready: false, dataSetting });
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  getDefaultStartEnd() {
    const end = new Date().getTime();
    const start = end - 24 * 60 * 60 * 1000;
    return { start, end };
  }

  getCurrentStartEnd() {
    const { selectionRange } = this.state;
    const start = Date.parse(selectionRange.startDate);
    const end = Date.parse(selectionRange.endDate);
    return { start, end };
  }

  async componentDidMount() {
    const datasets = await this.getDatasets();
    // console.info(`datasets: ${JSON.stringify(datasets)}`)
    this.setState({ datasets });
    // const {start, end} = this.getDefaultStartEnd();
    // await this.updateSensorData(start, end);
  }

  handleTrackerChanged = (t) => {
    this.setState({ tracker: t });
  };

  // Handles when the brush changes the timerange
  handleTimeRangeChange = (timerange) => {
    if (timerange) {
      this.setState({ timerange, brushrange: timerange });
    } else {
      this.setState({ timerange: null, brushrange: null });
    }
  };

  handleChartResize = (width) => {
    this.setState({ width });
  };

  handleActiveChange = (columnName) => {
    const dataSetting = this.state.dataSetting;
    dataSetting[columnName].show = !dataSetting[columnName].show;
    this.setState({ dataSetting });
  };

  renderChart = () => {
    if (this.state.mode === 'dataSetting') {
      return this.renderChannelsChart();
    }
    return <div>No chart</div>;
  };

  renderChannelsChart = () => {
    const {
      timerange,
      columnNames,
      dataSetting,
      maxTime,
      minTime,
      minDuration,
      series,
    } = this.state;
    const rows: any[] = [];

    for (let columnName of columnNames) {
      const charts: any[] = [];
      let displaySeries = series;

      charts.push(
        <LineChart
          key={`line-${columnName}`}
          axis={`${columnName}_axis`}
          series={displaySeries}
          columns={[columnName]}
          style={this.state.style}
          breakLine
        />
      );
      charts.push(
        <ScatterChart
          key={`scatter-${columnName}`}
          axis={`${columnName}_axis`}
          series={displaySeries}
          columns={[columnName]}
          style={this.state.style}
        />
      );
      charts.push(
        <Baseline
          key={`baseline-min-${columnName}`}
          axis={`${columnName}_axis`}
          style={baselineStyles.base}
          value={dataSetting[columnName].min}
        />
      );
      charts.push(
        <Baseline
          key={`baseline-avg-${columnName}`}
          axis={`${columnName}_axis`}
          style={baselineStyles.base}
          value={dataSetting[columnName].avg}
        />
      );
      charts.push(
        <Baseline
          key={`baseline-max-${columnName}`}
          axis={`${columnName}_axis`}
          style={baselineStyles.base}
          value={dataSetting[columnName].max}
        />
      );

      // Get the value at the current tracker position for the ValueAxis
      let value = '--';
      if (this.state.tracker) {
        const approx =
          (+this.state.tracker - +timerange.begin()) /
          (+timerange.end() - +timerange.begin());
        const ii = Math.floor(approx * series.size());
        const i = series.bisect(new Date(this.state.tracker), ii);
        const v = i < series.size() ? series.at(i).get(columnName) : null;
        if (v) {
          // @ts-ignore
          value = baseFormat(v);
        }
      }

      // Get the summary values for the LabelAxis
      const summary = [
        { label: 'Max', value: baseFormat(dataSetting[columnName].max) },
        { label: 'Avg', value: baseFormat(dataSetting[columnName].avg) },
        { label: 'Min', value: baseFormat(dataSetting[columnName].min) },
      ];

      rows.push(
        <ChartRow
          height="100"
          visible={dataSetting[columnName].show}
          key={`row-${columnName}`}>
          <LabelAxis
            id={`${columnName}_axis`}
            label={dataSetting[columnName].label}
            values={summary}
            min={
              dataSetting[columnName].min - dataSetting[columnName].min * 0.01
            }
            max={
              dataSetting[columnName].max + dataSetting[columnName].max * 0.01
            }
            width={140}
            type="linear"
            format=",.2f"
          />
          <Charts>{charts}</Charts>
          <ValueAxis
            id={`${columnName}_valueaxis`}
            value={value}
            detail={dataSetting[columnName].units}
            width={80}
            min={0}
            max={35}
          />
        </ChartRow>
      );
    }

    return (
      <ChartContainer
        timeRange={this.state.timerange}
        format="%H:%M"
        showGrid={true}
        // enablePanZoom
        maxTime={maxTime}
        minTime={minTime}
        minDuration={minDuration}
        trackerPosition={this.state.tracker}
        onTimeRangeChanged={this.handleTimeRangeChange}
        onChartResize={(width) => this.handleChartResize(width)}
        onTrackerChanged={this.handleTrackerChanged}>
        {rows}
      </ChartContainer>
    );
  };

  renderBrush = () => {
    const { dataSetting, series } = this.state;
    return (
      <ChartContainer
        timeRange={series.range()}
        format="%H:%M"
        trackerPosition={this.state.tracker}>
        <ChartRow height="100" debug={false}>
          <Brush
            timeRange={this.state.brushrange}
            allowSelectionClear
            onTimeRangeChanged={this.handleTimeRangeChange}
          />
          <YAxis
            id="axis1"
            label={dataSetting.v0.label}
            min={dataSetting.v0.min}
            max={dataSetting.v0.max}
            width={50}
            type="linear"
            format="d"
          />
          <Charts>
            <AreaChart
              axis="axis1"
              style={this.state.style.areaChartStyle()}
              columns={{ up: ['v0'], down: [] }}
              series={series}
            />
          </Charts>
        </ChartRow>
      </ChartContainer>
    );
  };

  renderMode = () => {
    const linkStyle = {
      fontWeight: 600,
      color: 'grey',
      cursor: 'default',
    };

    const linkStyleActive = {
      color: 'steelblue',
      cursor: 'pointer',
    };

    return (
      <div className="col-md-6" style={{ fontSize: 14, color: '#777' }}>
        <span
          style={
            this.state.mode !== 'dataSetting' ? linkStyleActive : linkStyle
          }
          onClick={() => this.setState({ mode: 'dataSetting' })}>
          Time-series sensor data
        </span>
      </div>
    );
  };

  renderDataSelection = () => {
    const { selectedDataset, datasets, selectionRange } = this.state;
    const datasetOptions = datasets.map((d) => ({ label: d.id, value: d.id }));
    // console.info(`datasetOptions: ${JSON.stringify(datasetOptions)}`)
    return (
      <div>
        <span>Select dataset</span>
        <Select
          options={datasetOptions}
          onChange={this.handleSelectDataset}
          value={selectedDataset}
          menuShouldScrollIntoView={true}
        />
        <DateRangePicker
          ranges={[selectionRange]}
          onChange={this.handleSelectDateRange}
          moveRangeOnFirstSelection={false}
          locale={ja}
        />
      </div>
    );
  };

  /**
   * ranges: {"selection":{"startDate":"2020-12-14T15:00:00.000Z","endDate":"2020-12-14T15:00:00.000Z","key":"selection"}}
   * @param ranges
   */
  handleSelectDateRange = async (ranges) => {
    const { selectedDataset } = this.state;
    const datasetId = selectedDataset.value;
    const start = Date.parse(ranges.selectionRange.startDate);
    const end = Date.parse(ranges.selectionRange.endDate);
    // console.info(`newRange: ${JSON.stringify(ranges.selectionRange)}`)
    this.setState({ selectionRange: ranges.selectionRange });
    if (datasetId !== undefined && start < end) {
      console.info(`datasetId: ${datasetId}, start: ${start}, end: ${end}`);
      this.setState({ loading: true });
      await this.updateSensorData(datasetId, start, end);
    }
  };

  handleSelectDataset = async (selectedOption: any, actionMeta: any) => {
    if (!actionMeta.action || actionMeta.action !== 'select-option') return;
    if (!selectedOption) return;
    this.setState({ selectedDataset: selectedOption });
    const datasetId = selectedOption.value;
    const { start, end } = this.getCurrentStartEnd();
    this.setState({ loading: true });
    await this.updateSensorData(datasetId, start, end);
  };

  render() {
    const { ready, loading, dataSetting, columnNames } = this.state;
    // handling
    if (!ready && !loading) {
      return <div>{this.renderDataSelection()}</div>;
    } else if (!ready && loading) {
      return (
        <div>
          {this.renderDataSelection()}
          <div>
            <div className="loader">Loading...</div>
          </div>
        </div>
      );
    }
    const chartStyle = {
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: '#DDD',
      paddingTop: 10,
      marginBottom: 10,
    };

    const brushStyle = {
      boxShadow: 'inset 0px 2px 5px -2px rgba(189, 189, 189, 0.75)',
      background: '#FEFEFE',
      paddingTop: 10,
    };

    // Generate the legend
    const legend = columnNames.map((columnName) => ({
      key: columnName,
      label: dataSetting[columnName].label,
      disabled: !dataSetting[columnName].show,
    }));

    return (
      <div>
        {this.renderDataSelection()}
        <div className="row">{this.renderMode()}</div>
        <div className="row">
          <div className="col-md-12">
            <hr />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <Legend
              type={this.state.mode === 'rollup' ? 'swatch' : 'line'}
              style={this.state.style}
              categories={legend}
              onSelectionChange={this.handleActiveChange}
            />
          </div>

          <div className="col-md-6">
            {this.state.tracker
              ? // @ts-ignore
                `${new Date(this.state.tracker).toISOString()}`
              : '-:--:--'}
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <hr />
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={chartStyle}>
            <Resizable>
              {ready ? this.renderChart() : <div>Loading.....</div>}
            </Resizable>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={brushStyle}>
            <Resizable>{ready ? this.renderBrush() : <div />}</Resizable>
          </div>
        </div>
      </div>
    );
  }
}

export default SensorData;
